import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { C_value, User } from '@app/shared/_models/common';
import {
	HrAbsence, HrEmployee, HrLeavePeriod, hrEmployeeNameOf, hrIsHrManager, hrMyEmployeeId,
} from '@app/shared/_models/hr';
import {
	C_VALUES_HR_ABSENCE_TYPE,
	EVENTTYP_DELETE, EVENTTYP_HR_LEAVE_PERIOD_ACK, EVENTTYP_INSERT,
	HR_ABSENCE_STATE_APPROVED, HR_LEAVE_PERIOD_CLOSED, HR_LEAVE_PERIOD_PROPOSED,
	OBJECTTYP_HR_LEAVE_PERIOD,
} from '@app/shared/constants';
import { hrPaidLeaveTypeIds } from '../hr-absence-catalog';
import { hrCountedLeaveDays } from '../hr-leave';
import {
	LeaveClosingRow, LeaveClosingStatus, LeaveClosingSummary, LeaveClosingWindow,
	buildLeaveClosingRow, daysLabel, employeeCarryoverOf, leaveClosingPayload, leaveClosingPeriodOf,
	leaveClosingSummary, leaveClosingWindowForPeriod, leavePeriodLabel, leavePeriodShortLabel,
} from '../hr-leave-closing';
import { DatRange, LeaveRules, leaveRulesOf } from '../hr-leave-rights';
import { frenchHolidaysOf } from '../hr-schedule-engine';
import { parseTz, todayStr } from '@app/shared/time';
import { ClosingDialogComponent, ClosingDialogData, ClosingDialogResult } from './closing-dialog/closing-dialog.component';

interface ClosingLine {
	row: LeaveClosingRow;
	employee: string;
	mine: boolean;
}

// CLÔTURE DE L'ANNÉE DE CONGÉS — /hr/leave-closing.
//
// Les congés payés ne se comptent pas par année civile : ils s'acquièrent sur une période de
// référence (du 1er juin au 31 mai à défaut d'accord) et se prennent sur la suivante. Deux années
// coexistent donc en permanence, et une fois par an il faut arrêter le compte de celle qui
// s'achève : ce qu'il en reste est reporté, perdu, ou payé.
//
// C'est le seul écran du module qui ÉCRIT un chiffre du compteur de congés — et c'est justement
// parce que ce chiffre n'en est pas un : c'est une décision, prise à une date, par des personnes
// nommées. Tout le reste (acquis, pris, solde) est recalculé ici même depuis les absences, et
// n'est stocké nulle part.
//
// DEUX LECTURES DU MÊME ÉCRAN :
//   - le responsable RH voit tout le personnel, arrête les comptes, et peut préparer la campagne
//     en un clic (une proposition par salarié, que chacun valide de son côté) ;
//   - le salarié ne voit que SA ligne, et n'y a qu'un geste : accepter le solde qu'on lui propose.
@Component({
	selector: 'app-hr-leave-closing',
	standalone: true,
	imports: [RouterLink, MenuCommonComponent, MatTableModule, MatButtonModule, MatIconModule,
		MatTooltipModule, MatDialogModule],
	templateUrl: './leave-closing.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './leave-closing.component.scss',
})
export class LeaveClosingComponent implements OnInit {

	public hrService = inject(HrService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private dialog = inject(MatDialog);

	public displayedColumns: string[] = ['employee', 'carried_in', 'acquired', 'taken', 'balance', 'decision', 'state', 'actions'];
	public lines: ClosingLine[] = [];
	public summary: LeaveClosingSummary = leaveClosingSummary([]);
	public isAdmin = false;
	public loading = false;

	public rules: LeaveRules = leaveRulesOf(null);
	public window: LeaveClosingWindow | null = null;
	/// La période affichée. Elle démarre sur celle que la campagne désigne, et se déplace d'année
	/// en année : une clôture oubliée en 2024 se rattrape sans reprise de données.
	public period: DatRange | null = null;

	private employees: HrEmployee[] = [];
	private users: User[] = [];
	private types: C_value[] = [];

	public readonly daysLabel = daysLabel;
	public readonly leavePeriodLabel = leavePeriodLabel;
	public readonly leavePeriodShortLabel = leavePeriodShortLabel;

	ngOnInit(): void {
		this.load();
	}

	// ============================================================
	// Chargement
	// ============================================================
	//
	// Les absences sont chargées sur DEUX périodes de référence : celle qu'on arrête, et la
	// précédente. La précédente ne sert pas au calcul (son report vient de sa clôture) mais elle
	// borne proprement les demandes à cheval sur le 31 mai — une semaine posée du 28 mai au 3 juin
	// se répartit alors sur les deux années, comme elle le doit.
	private load(): void {
		this.loading = true;
		forkJoin({
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			// Référentiel c_values : valuesOf$ porte les deux pièges du forkJoin (flux qui ne
			// complète jamais, échec qui fige l'écran) — cf. hr.service.ts.
			types: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			payrollRules: this.hrService.payrollRules$(),
		}).subscribe(({ employees, users, types, payrollRules }) => {
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);
			this.rules = payrollRules.leave;
			this.employees = employees;
			this.users = users;
			this.types = types as C_value[];
			// La période par défaut est celle que la campagne désigne — sauf si l'utilisateur en a
			// déjà choisi une autre (rechargement après une écriture).
			this.period ??= leaveClosingPeriodOf(todayStr(), this.rules);
			this.reload();
		});
	}

	/// Recharge les faits de la période affichée (absences + clôtures) et reconstruit les lignes.
	private reload(): void {
		const period = this.period!;
		this.loading = true;
		forkJoin({
			absences: this.hrService.getAbsences$(period.from, period.to, HR_ABSENCE_STATE_APPROVED),
			// TOUT l'historique des clôtures : le report entrant de la période affichée vient de la
			// clôture de la précédente, donc d'avant les bornes de la période.
			closings: this.hrService.getLeavePeriods$().pipe(catchError(() => of([] as HrLeavePeriod[]))),
		}).subscribe(({ absences, closings }) => {
			this.build(absences, closings);
			this.loading = false;
		});
	}

	private build(absences: HrAbsence[], closings: HrLeavePeriod[]): void {
		const period = this.period!;
		this.window = leaveClosingWindowForPeriod(period, todayStr(), this.rules);

		const cpTypes = new Set(hrPaidLeaveTypeIds(this.types));
		const holidays = new Map<string, string>();
		for (let y = +period.from.slice(0, 4) - 1; y <= +period.to.slice(0, 4) + 1; y++) {
			for (const [k, label] of frenchHolidaysOf(y)) holidays.set(k, label);
		}

		const myId = this.myEmployeeId();
		const targets = this.isAdmin
			// Une fiche désactivée EN COURS DE PÉRIODE garde un solde à solder : c'est même là que
			// l'indemnité compensatrice se décide. On ne l'écarte que si elle n'a rien à arbitrer.
			? this.employees
			: this.employees.filter(e => e.id === myId);

		const lines: ClosingLine[] = [];
		for (const emp of targets) {
			const mine = emp.id === myId;
			const own = absences.filter(a => a.id_employee === emp.id && a.typ != null && cpTypes.has(a.typ));
			const taken = hrCountedLeaveDays(own, period.from, period.to, holidays)
				.map(d => ({ dat: d.dat, days: d.days, waived: false }));
			const row = buildLeaveClosingRow({
				id_employee: emp.id,
				hire_date: emp.hire_date ?? null,
				// La sortie borne l'acquisition (L3141-3) — c'est ici que le solde de tout compte se joue.
				leave_date: emp.leave_date ?? null,
				// Règle de report propre à la fiche, quand elle en porte une (accord individuel).
				// Elle ne décide de rien : elle pré-remplit l'arrêté.
				carryover: employeeCarryoverOf(emp.data),
				taken,
				closings: closings.filter(c => c.id_employee === emp.id),
			}, this.window, this.rules);

			// Une fiche inactive SANS solde ni clôture n'a rien à faire dans la liste : elle n'ajoute
			// que du bruit à une campagne qui se lit salarié par salarié.
			if (emp.is_active === false && row.balance_days === 0 && !row.closing) continue;
			lines.push({ row, employee: hrEmployeeNameOf(emp, this.users), mine });
		}

		this.lines = lines.sort((a, b) => a.employee.localeCompare(b.employee));
		this.summary = leaveClosingSummary(this.lines.map(l => l.row));
	}

	private myEmployeeId(): number | null {
		return hrMyEmployeeId(this.loginService.loggeduser, this.employees);
	}

	// ============================================================
	// Navigation d'une année de congés à l'autre
	// ============================================================

	onShiftPeriod(delta: number): void {
		const from = (+this.period!.from.slice(0, 4) + delta) + this.period!.from.slice(4);
		const to = (+this.period!.to.slice(0, 4) + delta) + this.period!.to.slice(4);
		this.period = { from, to };
		this.reload();
	}

	onCurrentPeriod(): void {
		this.period = leaveClosingPeriodOf(todayStr(), this.rules);
		this.reload();
	}

	// ============================================================
	// Libellés d'état
	// ============================================================

	statusLabel(s: LeaveClosingStatus): string {
		switch (s) {
			case 'closed': return 'Clôturée';
			case 'accepted': return 'Validée par le salarié';
			case 'late': return 'En retard';
			case 'open': return 'À arrêter';
			default: return 'À venir';
		}
	}

	statusHint(line: ClosingLine): string {
		const c = line.row.closing;
		switch (line.row.status) {
			case 'closed':
				return 'Arrêtée le ' + this.tsLabel(c?.datclosed)
					+ (c?.datack ? ' — acceptée par le salarié le ' + this.tsLabel(c.datack) : '');
			case 'accepted':
				return 'Le salarié a validé son solde le ' + this.tsLabel(c?.datack) + ' — reste à arrêter le compte';
			case 'late':
				return 'La période est close depuis le ' + this.frDat(line.row.period.to) + ' et le compte n\'est pas arrêté';
			case 'open':
				return 'La période s\'achève le ' + this.frDat(line.row.period.to);
			default:
				return 'La campagne s\'ouvrira le ' + this.frDat(this.window?.opens ?? '');
		}
	}

	/// Ce que la clôture a décidé, en une ligne : « 5 j reportés · 2 j perdus ».
	decisionLabel(row: LeaveClosingRow): string {
		const c = row.closing;
		if (!c) return '—';
		const parts: string[] = [];
		if (+c.carried_days > 0) parts.push(daysLabel(+c.carried_days) + ' reportés');
		if (+c.lost_days > 0) parts.push(daysLabel(+c.lost_days) + ' perdus');
		if (+c.paid_days > 0) parts.push(daysLabel(+c.paid_days) + ' payés');
		return parts.length ? parts.join(' · ') : 'Rien à reporter';
	}

	// Jour MÉTIER de l'instant (datclosed/datack) — pas celui du poste : un navigateur réglé à
	// l'ouest afficherait la veille pour un arrêté signé dans les premières heures de Paris.
	private tsLabel(ts: string | null | undefined): string {
		return ts ? (parseTz(ts)?.format('DD/MM/YYYY') ?? '—') : '—';
	}

	private frDat(dat: string): string {
		return dat ? (dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4)) : '—';
	}

	// ============================================================
	// Arrêter un compte
	// ============================================================

	canClose(line: ClosingLine): boolean {
		return this.isAdmin && this.rules.closing_enabled;
	}

	/// Le salarié n'accepte que ce qu'on lui a PROPOSÉ, et une seule fois : accepter deux fois n'a
	/// pas de sens, et accepter avant qu'on ait préparé le compte non plus.
	canAck(line: ClosingLine): boolean {
		return line.mine
			&& this.rules.closing_employee_ack
			&& line.row.closing?.state === HR_LEAVE_PERIOD_PROPOSED;
	}

	onClose(line: ClosingLine): void {
		const data: ClosingDialogData = { row: line.row, employee: line.employee, rules: this.rules };
		// 720 px : les trois cases de l'arbitrage tiennent sur une ligne avec leur libellé et leur
		// explication, sans que rien ne se chevauche. `maxWidth` laisse le dialogue se rétrécir sur
		// un écran étroit (le défaut Material, 80vw, couperait le texte légal sur portable).
		this.dialog.open(ClosingDialogComponent, { data, width: '720px', maxWidth: '95vw' }).afterClosed()
			.subscribe((result: ClosingDialogResult) => {
				if (!result) return;
				const payload = leaveClosingPayload(line.row, result, result.state);
				this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_LEAVE_PERIOD, payload).subscribe({
					next: () => {
						this.alertService.success(result.state === HR_LEAVE_PERIOD_CLOSED
							? 'Compte arrêté' : 'Solde soumis au salarié');
						this.reload();
					},
					error: (e) => this.alertService.error(hrErrorMessage(e)),
				});
			});
	}

	/// PRÉPARER LA CAMPAGNE — une proposition par salarié, d'après le réglage de l'établissement.
	///
	/// C'est le geste qui lance la campagne : chacun reçoit son solde à valider, et le responsable
	/// n'a plus qu'à arrêter les comptes acceptés. Sans lui, il faudrait ouvrir vingt dialogues
	/// pour dire vingt fois la même chose.
	onPrepareAll(): void {
		const pending = this.lines.filter(l => !l.row.closing && l.row.balance_days !== 0);
		if (pending.length === 0) {
			this.alertService.error('Aucun solde à proposer : tout est déjà arrêté ou à zéro.');
			return;
		}
		const carry = this.rules.carryover_default === 'carry';
		Swal.fire({
			title: 'Proposer ' + pending.length + ' solde(s) ?',
			html: 'Chaque salarié recevra son solde de l\'année ' + leavePeriodShortLabel(this.period!)
				+ ', <b>' + (carry ? 'reporté' : 'perdu') + '</b> par défaut, et pourra le valider depuis son écran Congés.'
				+ '<br><br>Rien n\'est arrêté : vous clôturerez ensuite, ligne par ligne, et vous pouvez encore tout corriger.',
			icon: 'question', showCancelButton: true, confirmButtonText: 'Proposer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.submitBatch(pending, HR_LEAVE_PERIOD_PROPOSED, 'Solde(s) proposé(s)');
		});
	}

	/// ARRÊTER LES COMPTES ACCEPTÉS — la contrepartie : ce que le salarié a validé se clôture d'un
	/// bloc, à l'identique de ce qu'il a accepté. On ne reprend jamais un chiffre qu'il n'a pas vu.
	onCloseAccepted(): void {
		const accepted = this.lines.filter(l => l.row.status === 'accepted');
		if (accepted.length === 0) {
			this.alertService.error('Aucun solde validé par un salarié n\'attend d\'être arrêté.');
			return;
		}
		Swal.fire({
			title: 'Arrêter ' + accepted.length + ' compte(s) ?',
			text: 'Ces soldes ont été validés par les intéressés : ils seront clôturés tels qu\'ils les ont acceptés.',
			icon: 'question', showCancelButton: true, confirmButtonText: 'Clôturer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.submitBatch(accepted, HR_LEAVE_PERIOD_CLOSED, 'Compte(s) arrêté(s)');
		});
	}

	// Un event par salarié — le serveur upserte sur (salarié, période). Les refus sont comptés et
	// annoncés : « 18 sur 20 » vaut mieux qu'un « enregistré » qui cache deux échecs.
	private submitBatch(lines: ClosingLine[], state: number, done: string): void {
		let ok = 0, ko = 0;
		const finish = () => {
			if (ok + ko < lines.length) return;
			if (ko > 0) this.alertService.error(ko + ' refus sur ' + lines.length + ' — voir la console');
			else this.alertService.success(done + ' : ' + ok);
			this.reload();
		};
		for (const line of lines) {
			const c = line.row.closing;
			// Un compte ACCEPTÉ se clôture à l'identique ; un compte neuf part sur la proposition.
			const form = c
				? { carried_days: +c.carried_days || 0, lost_days: +c.lost_days || 0, paid_days: +c.paid_days || 0, comment: c.comment }
				: { carried_days: line.row.proposed_carry, lost_days: line.row.proposed_lost, paid_days: 0, comment: null };
			const payload = leaveClosingPayload(line.row, form, state);
			this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_LEAVE_PERIOD, payload).subscribe({
				next: () => { ok++; finish(); },
				error: (e) => { ko++; console.error('[clôture] ' + line.employee, e?.error ?? e); finish(); },
			});
		}
	}

	/// LE SALARIÉ ACCEPTE SON SOLDE — un seul geste, et aucun chiffre à saisir : il signe celui
	/// qu'on lui propose. S'il n'est pas d'accord, il ne clique pas et le dit — le responsable
	/// rouvrira le compte.
	onAck(line: ClosingLine): void {
		const row = line.row;
		const c = row.closing!;
		Swal.fire({
			title: 'Valider votre solde de congés ?',
			html: 'Année ' + leavePeriodShortLabel(row.period) + ' — <b>' + this.decisionLabel(row) + '</b>.'
				+ '<br><br>Votre accord est enregistré avec la date. Si ce solde vous paraît faux, ne validez pas :'
				+ ' signalez-le au responsable, qui rouvrira le compte.',
			icon: 'question', showCancelButton: true, confirmButtonText: 'Je valide', cancelButtonText: 'Pas maintenant',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_HR_LEAVE_PERIOD_ACK, OBJECTTYP_HR_LEAVE_PERIOD, { id: c.id }).subscribe({
				next: () => { this.alertService.success('Solde validé'); this.reload(); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	/// RÉOUVERTURE — le seul moyen de revenir sur un arrêté contesté : on l'efface, le solde
	/// redevient un calcul, et on reprend l'arbitrage. c_event garde la trace de l'effacement.
	onReopen(line: ClosingLine): void {
		const c = line.row.closing;
		if (!c) return;
		Swal.fire({
			title: 'Rouvrir ce compte ?',
			html: '<b>' + line.employee + '</b> — année ' + leavePeriodShortLabel(line.row.period) + '.'
				+ '<br><br>L\'arrêté est effacé : le report qu\'il transmettait à l\'année suivante disparaît, et'
				+ ' le solde redevient un calcul. L\'accord du salarié, s\'il l\'avait donné, est perdu avec lui.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Rouvrir', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_LEAVE_PERIOD, { id: c.id }).subscribe({
				next: () => { this.alertService.success('Compte rouvert'); this.reload(); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}
}
